import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaCity, 
  FaEdit,
  FaArrowLeft,
  FaCalendarAlt,
  FaIdCard,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaTimes,
  FaLock
} from 'react-icons/fa';
import { ref, get, update } from 'firebase/database';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError('');

      // Get user mobile from localStorage or AuthContext
      const userMobile = localStorage.getItem('userMobile') || user?.mobilenumber;
      
      if (!userMobile) {
        setError('User mobile number not found. Please login again.');
        return;
      }

      // Fetch user data from Firebase Realtime Database
      const userRef = ref(db, `Dr/users/${userMobile}`);
      const userSnapshot = await get(userRef);

      if (userSnapshot.exists()) {
        const data = userSnapshot.val();
        setUserData(data);
        setEditFormData(data); // Initialize edit form with current data
        console.log('User data fetched successfully:', data);
      } else {
        setError('User profile not found. Please complete your registration.');
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load profile data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form data
      setEditFormData(userData);
      setEditErrors({});
    }
    setIsEditing(!isEditing);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific error
    if (editErrors[name]) {
      setEditErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateEditForm = () => {
    const newErrors = {};

    if (!editFormData.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (editFormData.fullName.length < 3) {
      newErrors.fullName = 'Name must be at least 3 characters';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editFormData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(editFormData.email)) {
      newErrors.email = 'Invalid email address';
    }

    const mobileRegex = /^[6-9]\d{9}$/;
    if (!editFormData.mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!mobileRegex.test(editFormData.mobile)) {
      newErrors.mobile = 'Enter valid 10-digit mobile number';
    }

    if (!editFormData.address?.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!editFormData.city?.trim()) {
      newErrors.city = 'City is required';
    }

    const pincodeRegex = /^\d{6}$/;
    if (!editFormData.pincode) {
      newErrors.pincode = 'Pincode is required';
    } else if (!pincodeRegex.test(editFormData.pincode)) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }

    return newErrors;
  };

  const handleSaveChanges = async () => {
    const validationErrors = validateEditForm();
    if (Object.keys(validationErrors).length > 0) {
      setEditErrors(validationErrors);
      return;
    }

    setIsSaving(true);
    try {
      const userMobile = localStorage.getItem('userMobile') || user?.mobilenumber;
      
      // Update user data in Firebase
      const userRef = ref(db, `Dr/users/${userMobile}`);
      await update(userRef, {
        fullName: editFormData.fullName,
        email: editFormData.email,
        mobile: editFormData.mobile,
        address: editFormData.address,
        city: editFormData.city,
        pincode: editFormData.pincode,
        password: editFormData.password,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setUserData(editFormData);
      
      // Update localStorage if mobile changed
      if (editFormData.mobile !== userMobile) {
        localStorage.setItem('userMobile', editFormData.mobile);
        localStorage.setItem('userName', editFormData.fullName);
      }

      setIsEditing(false);
      setEditErrors({});
      
      // Show success message (you could add a toast notification here)
      alert('Profile updated successfully!');
      
    } catch (error) {
      console.error('Error updating profile:', error);
      setEditErrors({ submit: 'Failed to update profile. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center">
            <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaUser className="text-red-600 text-2xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Profile Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={fetchUserData}
                className="w-full bg-teal-600 text-white py-3 rounded-xl font-semibold hover:bg-teal-700 transition-colors"
              >
                Try Again
              </button>
              <Link
                to="/"
                className="block w-full bg-gray-200 text-gray-800 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors text-center"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium mb-6"
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-8 text-white">
              <div className="flex items-center space-x-6">
                <div className="bg-white/20 backdrop-blur-sm w-24 h-24 rounded-full flex items-center justify-center">
                  <FaUser className="text-4xl text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold mb-2">{userData?.fullName || 'User'}</h2>
                  <p className="text-teal-100 text-lg">Patient Account</p>
                  <div className="flex items-center mt-2 text-teal-200">
                    <FaIdCard className="mr-2" />
                    <span>ID: {userData?.uid || 'N/A'}</span>
                  </div>
                </div>
                <div className="text-right">
                  {isEditing ? (
                    <div className="flex space-x-2">
                      <button 
                        onClick={handleSaveChanges}
                        disabled={isSaving}
                        className="bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                      >
                        <FaSave />
                        <span>{isSaving ? 'Saving...' : 'Save'}</span>
                      </button>
                      <button 
                        onClick={handleEditToggle}
                        className="bg-red-600 text-white px-4 py-2 rounded-xl hover:bg-red-700 transition-colors flex items-center space-x-2"
                      >
                        <FaTimes />
                        <span>Cancel</span>
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={handleEditToggle}
                      className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl hover:bg-white/30 transition-colors flex items-center space-x-2"
                    >
                      <FaEdit />
                      <span>Edit Profile</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="p-8">
              {editErrors.submit && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
                  {editErrors.submit}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Personal Information */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <FaUser className="mr-2 text-teal-600" />
                    Personal Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <FaUser className="text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Full Name</p>
                        {isEditing ? (
                          <div>
                            <input
                              type="text"
                              name="fullName"
                              value={editFormData.fullName || ''}
                              onChange={handleEditChange}
                              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                                editErrors.fullName ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="Enter your full name"
                            />
                            {editErrors.fullName && <p className="mt-1 text-sm text-red-600">{editErrors.fullName}</p>}
                          </div>
                        ) : (
                          <p className="font-medium text-gray-800">{userData?.fullName || 'N/A'}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <FaEnvelope className="text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Email Address</p>
                        {isEditing ? (
                          <div>
                            <input
                              type="email"
                              name="email"
                              value={editFormData.email || ''}
                              onChange={handleEditChange}
                              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                                editErrors.email ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="Enter your email"
                            />
                            {editErrors.email && <p className="mt-1 text-sm text-red-600">{editErrors.email}</p>}
                          </div>
                        ) : (
                          <p className="font-medium text-gray-800">{userData?.email || 'N/A'}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <FaPhone className="text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Mobile Number</p>
                        {isEditing ? (
                          <div>
                            <div className="flex">
                              <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 text-gray-600">+91</span>
                              <input
                                type="tel"
                                name="mobile"
                                value={editFormData.mobile || ''}
                                onChange={handleEditChange}
                                maxLength="10"
                                className={`flex-1 p-2 border rounded-r-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                                  editErrors.mobile ? 'border-red-300' : 'border-gray-300'
                                }`}
                                placeholder="9876543210"
                              />
                            </div>
                            {editErrors.mobile && <p className="mt-1 text-sm text-red-600">{editErrors.mobile}</p>}
                          </div>
                        ) : (
                          <p className="font-medium text-gray-800">+91 {userData?.mobile || 'N/A'}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <FaLock className="text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Password</p>
                        {isEditing ? (
                          <div>
                            <div className="relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={editFormData.password || ''}
                                onChange={handleEditChange}
                                className="w-full p-2 pr-10 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 border-gray-300"
                                placeholder="Enter your password"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-800">
                              {showPassword ? userData?.password : '••••••••'}
                            </p>
                            <button
                              onClick={() => setShowPassword(!showPassword)}
                              className="text-gray-500 hover:text-gray-700 p-1"
                              title={showPassword ? "Hide password" : "Show password"}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <FaCalendarAlt className="text-gray-400 mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">Member Since</p>
                        <p className="font-medium text-gray-800">
                          {userData?.createdAt ? 
                            new Date(userData.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : 'N/A'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-teal-600" />
                    Address Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <FaMapMarkerAlt className="text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Street Address</p>
                        {isEditing ? (
                          <div>
                            <textarea
                              name="address"
                              value={editFormData.address || ''}
                              onChange={handleEditChange}
                              rows="3"
                              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                                editErrors.address ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="Enter your address"
                            />
                            {editErrors.address && <p className="mt-1 text-sm text-red-600">{editErrors.address}</p>}
                          </div>
                        ) : (
                          <p className="font-medium text-gray-800">{userData?.address || 'N/A'}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <FaCity className="text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">City</p>
                        {isEditing ? (
                          <div>
                            <input
                              type="text"
                              name="city"
                              value={editFormData.city || ''}
                              onChange={handleEditChange}
                              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                                editErrors.city ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="Enter your city"
                            />
                            {editErrors.city && <p className="mt-1 text-sm text-red-600">{editErrors.city}</p>}
                          </div>
                        ) : (
                          <p className="font-medium text-gray-800">{userData?.city || 'N/A'}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <FaIdCard className="text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Pincode</p>
                        {isEditing ? (
                          <div>
                            <input
                              type="text"
                              name="pincode"
                              value={editFormData.pincode || ''}
                              onChange={handleEditChange}
                              maxLength="6"
                              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                                editErrors.pincode ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="Enter pincode"
                            />
                            {editErrors.pincode && <p className="mt-1 text-sm text-red-600">{editErrors.pincode}</p>}
                          </div>
                        ) : (
                          <p className="font-medium text-gray-800">{userData?.pincode || 'N/A'}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
